'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';

// --- CONFIGURATION ---
interface NodeConfig {
    id: string;
    label: string;
    icon: string;
    color: string;
    gradient: string;
    shimmerColor: string;
}

const NODES: NodeConfig[] = [
    { id: 'seo', label: 'Local SEO', icon: 'MapPinIcon', color: 'text-blue-600', gradient: 'from-blue-400 to-blue-600', shimmerColor: '#2563eb' },
    { id: 'content', label: 'Content Strategy', icon: 'DocumentTextIcon', color: 'text-rose-500', gradient: 'from-rose-400 to-red-500', shimmerColor: '#f43f5e' },
    { id: 'reputation', label: 'Reputation', icon: 'StarIcon', color: 'text-amber-500', gradient: 'from-amber-400 to-orange-500', shimmerColor: '#f59e0b' },
    { id: 'social', label: 'Google Business Profile', icon: 'BuildingStorefrontIcon', color: 'text-blue-500', gradient: 'from-blue-400 to-cyan-500', shimmerColor: '#3b82f6' },
    { id: 'analytics', label: 'Analytics', icon: 'PresentationChartLineIcon', color: 'text-indigo-600', gradient: 'from-indigo-400 to-violet-500', shimmerColor: '#6366f1' },
];

// Simplified Shimmer Path using declarative animation
function ShimmerPath({ path, color }: { path: string, color: string }) {
    return (
        <motion.path
            d={path}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="20 200" // Small dash, large gap
            initial={{ strokeDashoffset: 220 }}
            animate={{ strokeDashoffset: 0 }}
            transition={{
                duration: 2,
                ease: "linear",
                repeat: Infinity,
                repeatDelay: 0
            }}
        />
    );
}

const MobileStrategyPlayground = () => {
    const [activeNodes, setActiveNodes] = useState<string[]>(['seo', 'content', 'reputation', 'social', 'analytics']);
    // No manual animation loop needed now

    const toggleNode = (id: string) => {
        if (activeNodes.includes(id)) {
            setActiveNodes(prev => prev.filter(n => n !== id));
        } else {
            setActiveNodes(prev => [...prev, id]);
        }
    };

    // --- VERTICAL DIMENSIONS ---
    const width = 350; // Mobile viewport width approx
    // We essentially need a "diagram" height.
    // Core (Top) -> Spacing -> List of Items
    const startY = 60; // Center of Brand Core node
    const endX = width - 60; // Right side alignment for nodes
    const itemHeight = 84;
    const listStartY = 160;

    // We generate an SVG container large enough to hold everything
    const playgroundHeight = listStartY + (NODES.length * itemHeight);

    const generatePath = (index: number) => {
        // Source: Top Center (width/2, startY)
        // Target: Left side of the item icon.
        // Item X = 40 (padding) + 32 (icon radius) roughly. 
        // Or actually, let's connect to the LEFT side of the item row.

        const sourceX = 60; // Center of Brand Core (Aligned Left now?) 
        // User image shows Source on Left, Fan out to Right. 
        // On Mobile, vertical stacking usually implies Source Top, Targets Bottom.

        // Let's try: Source Top Center. Targets List Below.
        const sx = width / 2;
        const sy = startY + 50; // Bottom of Source Box

        const tx = 40; // Left side of the target item row
        const ty = listStartY + (index * itemHeight) + 30; // Center of target icon

        // Vertical Sigmoid
        // M sx,sy C sx,sy+50 tx-50,ty tx,ty
        // Make it flow down and then curve left/right? 
        // Actually, listing items vertically...

        // Let's do: Brand Core (Top Left) -> Fan Out Right/Down (Diagonal List)
        // Similar to the image but squeezed.

        return `M ${60},${60} Q ${60},${ty} ${width / 2},${ty}`;

        // Scratch that. Let's do a simple Tree:
        // Top Center -> Down -> Split to items.

        const targetY = listStartY + (index * itemHeight) + 28; // Center of icon vertically
        // Curve:
        return `M ${width / 2},${80} C ${width / 2},${(80 + targetY) / 2} ${40},${(80 + targetY) / 2} ${50},${targetY}`;
    };


    return (
        <div className="w-full bg-white relative overflow-hidden py-10 flex flex-col items-center">
            {/* Header */}
            <div className="text-center mb-10 px-6 relative z-10">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">
                    Brand Visibility <span className="text-blue-600">Network</span>
                </h2>
                <p className="text-sm text-slate-500">
                    Integrated services driving your brand core.
                </p>
            </div>

            <div className="relative w-full max-w-[360px] h-[600px]">
                {/* SVG Layer */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none z-0">
                    {NODES.map((node, i) => {
                        // Calculate specific path per node
                        // Source: Top Center
                        const sx = 180; // center of 360
                        const sy = 90; // Bottom of Brand Core

                        // Target: The Icon Center
                        // Icons are positioned using flex layout below, we need their coordinates.
                        // Let's assume absolute positioning for precision like desktop version.

                        const ty = 160 + (i * 80) + 28; // listStart + offset + halfIcon
                        const tx = 60; // Center X of the icon (left aligned)

                        // Path: Vertical curve from Top Center to Left Side Item
                        const path = `M ${sx},${sy} C ${sx},${ty} ${sx},${ty} ${tx + 30},${ty}`;

                        return (
                            <React.Fragment key={`mobile-path-${i}`}>
                                <path
                                    d={path}
                                    stroke="#e2e8f0"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                />
                                {activeNodes.includes(node.id) && (
                                    <ShimmerPath
                                        path={path}
                                        color={node.shimmerColor}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}
                </svg>

                {/* 1. BRAND CORE (Top Center) */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20">
                    <div className="w-20 h-20 rounded-2xl bg-white shadow-xl border-4 border-white flex flex-col items-center justify-center relative">
                        <div className="absolute inset-0 bg-blue-500/10 blur-xl rounded-full" />
                        <div className="relative z-10 text-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-violet-600 rounded-xl flex items-center justify-center mx-auto mb-1 text-white shadow-lg">
                                <Icon name="SparklesIcon" className="w-5 h-5" />
                            </div>
                            <div className="text-[8px] font-bold text-slate-500 uppercase tracking-wide">Brand Core</div>
                        </div>
                    </div>
                </div>

                {/* 2. NODES LIST (Vertical Stack) */}
                <div className="absolute top-[160px] left-0 w-full px-6 flex flex-col gap-5">
                    {NODES.map((node) => {
                        const isActive = activeNodes.includes(node.id);
                        return (
                            <button
                                key={node.id}
                                onClick={() => toggleNode(node.id)}
                                className={`group flex items-center gap-4 transition-all duration-300 w-full ${isActive ? 'translate-x-0' : 'translate-x-2 opacity-70 grayscale'}`}
                            >
                                {/* Icon */}
                                <div className={`w-14 h-14 shrink-0 rounded-full flex items-center justify-center bg-white shadow-md border-2 transition-all z-20 relative ${isActive ? 'border-white ring-2 ring-opacity-20 ' + node.color.replace('text-', 'ring-') : 'border-slate-100'}`}>
                                    <Icon name={node.icon as any} className={`w-6 h-6 transition-colors ${isActive ? node.color : 'text-slate-400'}`} />
                                </div>

                                {/* Label */}
                                <div className="text-left">
                                    <div className={`text-sm font-bold ${isActive ? 'text-slate-900' : 'text-slate-500'}`}>
                                        {node.label}
                                    </div>
                                    <div className="text-[10px] text-slate-400">
                                        {isActive ? 'Active' : 'Tap to connect'}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MobileStrategyPlayground;
